import { IComparisonFieldSetting, IOffer, IOfferComparisonFieldValue } from '@models/winnerSelection/ITableDemand';

/**
 * Эталон для подсветки лучшего значения:
 * - min/max — соответствующий экстремум;
 * - target — минимальное абсолютное отклонение от целевого значения;
 * - none — null (ранжирование отключено).
 */
export const buildComparisonBenchmarks = (
    fields: IComparisonFieldSetting[],
    offers: IOffer[],
): Record<string, number | null> =>
    Object.fromEntries(
        fields.map((field) => {
            if (field.comparison === 'none') return [field.key, null];

            const values = offers
                .filter((offer) => !offer.isWithdrawn)
                .map((offer) => offer.comparisonFields?.[field.key])
                .filter(
                    (value): value is IOfferComparisonFieldValue & { compareValue: number } =>
                        value?.compareValue != null,
                );

            if (values.length === 0) return [field.key, null];
            if (field.comparison === 'min') {
                return [field.key, Math.min(...values.map((value) => value.compareValue))];
            }
            if (field.comparison === 'max') {
                return [field.key, Math.max(...values.map((value) => value.compareValue))];
            }

            const distances = values
                .filter(
                    (value): value is IOfferComparisonFieldValue & { compareValue: number; targetValue: number } =>
                        value.targetValue != null,
                )
                .map((value) => Math.abs(value.compareValue - value.targetValue));
            return [field.key, distances.length > 0 ? Math.min(...distances) : null];
        }),
    );

export const isBestComparisonValue = (
    field: IComparisonFieldSetting,
    compareValue: number | null | undefined,
    targetValue: number | null | undefined,
    benchmark: number | null,
): boolean => {
    if (compareValue == null || benchmark == null || field.comparison === 'none') return false;
    const current =
        field.comparison === 'target' && targetValue != null ? Math.abs(compareValue - targetValue) : compareValue;
    return Math.abs(current - benchmark) < 10 ** -(field.precision + 2);
};
